import { graphql, useFragment } from 'react-relay';

import type { DirectoryFeeBlock_agent$key } from './__generated__/DirectoryFeeBlock_agent.graphql';
import DirectoryAgentClaimRequestDialog from './DirectoryAgentClaimRequestDialog';
import DirectorySourceLinks from './DirectorySourceLinks';

type DirectoryFeeBlockProps = {
  agent: DirectoryFeeBlock_agent$key;
};

const DirectoryFeeBlock = ({ agent: agentKey }: DirectoryFeeBlockProps) => {
  const agent = useFragment(
    graphql`
      fragment DirectoryFeeBlock_agent on LandlordDirectoryAgent {
        id
        name
        slug
        fees {
          management {
            amountPence
            percent
            sourceUrls
          }
          rentCollection {
            amountPence
            percent
            sourceUrls
          }
          tenantFind {
            amountPence
            percent
            sourceUrls
          }
          letOnly {
            amountPence
            percent
            sourceUrls
          }
          renewal {
            amountPence
            percent
            sourceUrls
          }
          inventory {
            amountPence
            percent
            sourceUrls
          }
          feesIncludeVat
        }
        sources {
          feesIncludeVat
        }
      }
    `,
    agentKey,
  );
  const fees = agent.fees;
  const feeRows = [
    { label: 'Full management', fee: fees?.management },
    { label: 'Rent collection', fee: fees?.rentCollection },
    { label: 'Tenant find', fee: fees?.tenantFind },
    { label: 'Let only', fee: fees?.letOnly },
    { label: 'Renewal', fee: fees?.renewal },
    { label: 'Inventory', fee: fees?.inventory },
  ].filter(({ fee }) => fee?.amountPence != null || fee?.percent != null);

  return (
    <section
      aria-labelledby="agent-fees-heading"
      className="surface-card flex flex-col gap-5 p-5 md:p-7"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2
          className="m-0 text-2xl font-medium tracking-[-0.025em]"
          id="agent-fees-heading"
        >
          Published fees
        </h2>
        <span className="rounded-full bg-accent-tint px-3 py-1 text-xs font-medium text-accent-strong">
          Unverified
        </span>
      </div>
      <p className="m-0 text-sm leading-relaxed text-ink-soft">
        These figures were collected from public sources and have not been
        confirmed by the agent. Ask for a written quote before deciding.
      </p>
      {feeRows.length === 0 && agent.slug ? (
        <div className="flex flex-col items-start gap-4 rounded-2xl bg-accent-tint p-5">
          <div className="flex flex-col gap-1.5">
            <p className="m-0 font-medium text-ink">Work at this agency?</p>
            <p className="m-0 max-w-lg text-sm leading-relaxed text-ink-soft">
              Claim your free profile to add fees and help landlords find and
              compare you in local searches.
            </p>
          </div>
          <DirectoryAgentClaimRequestDialog
            agentId={agent.id}
            agentName={agent.name ?? 'this agent'}
            agentSlug={agent.slug}
            triggerLabel="Get featured in local searches"
          />
        </div>
      ) : (
        <dl className="m-0 grid gap-3">
          {feeRows.map(({ label, fee }) => (
            <div
              className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-xl bg-canvas p-4"
              key={label}
            >
              <dt className="flex min-w-0 flex-col items-start gap-1.5 text-sm text-ink-soft">
                {label}
                <DirectorySourceLinks label={label} urls={fee?.sourceUrls} />
              </dt>
              <dd className="m-0 font-medium tabular-nums">
                {formatFee(fee?.amountPence, fee?.percent)}
              </dd>
            </div>
          ))}
        </dl>
      )}
      {fees?.feesIncludeVat == null ? null : (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span>
            VAT {fees.feesIncludeVat ? 'is included' : 'is not included'}
          </span>
          <DirectorySourceLinks
            label="VAT"
            urls={agent.sources?.feesIncludeVat}
          />
        </div>
      )}
    </section>
  );
};

const formatFee = (
  amountPence: number | null | undefined,
  percent: string | null | undefined,
) => {
  if (percent != null) {
    return `${percent}%`;
  }

  if (amountPence != null) {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      maximumFractionDigits: 0,
    }).format(amountPence / 100);
  }

  return 'Not published';
};

export default DirectoryFeeBlock;
